import React, { useEffect, useRef, useState } from "react";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ApiMethods from "../../../../ApiManager/ApiMethods";
import DeletePopUp from "./DeletePopUp";
import { useAlert } from "../../../context/AlertContext";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import "./profilePhotoPicker.css";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_SIZE = 5 * 1024 * 1024; // 5MB, matches the backend's own limit

// Tablet/Desktop gets a centered modal; Mobile gets a bottom action sheet,
// matching the reference User App design. Same breakpoint used across this
// app's other responsive popups (NewAppointmentFlow, PopUpCard, PopUpTest).
const useIsDesktopTablet = () => {
  const [isDesktopTablet, setIsDesktopTablet] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktopTablet(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isDesktopTablet;
};

// Dispatched after a successful upload/removal so any other mounted
// component showing this employee's avatar (AppShell sidebar/header,
// Settings/Profil screen) can update immediately, without a page refresh.
export const PHOTO_UPDATED_EVENT = "hf-employee-photo-updated";

const ProfilePhotoPicker = ({ open, onClose, currentPhotoUrl, onUpdated }) => {
  const { showAlert } = useAlert();
  const isDesktopTablet = useIsDesktopTablet();
  // Always mounted (returns null when !open rather than being conditionally rendered by its
  // parent), so the lock itself must be gated on `open`, not just mount.
  useLockBodyScroll(open);

  const [stage, setStage] = useState("menu"); // "menu" | "preview" | "removeConfirm"
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStage("menu");
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
  }, [open]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!open || !isDesktopTablet) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, isDesktopTablet, onClose]);

  if (!open) return null;

  const resetToMenu = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
    setStage("menu");
  };

  const handleFileSelected = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    // No file: user canceled the picker or a camera/gallery permission was
    // denied — the browser gives no way to tell those apart, so just stay
    // on the menu rather than guessing at an error message.
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Nepodržan tip fajla. Dozvoljeni formati su JPG, PNG i WEBP.");
      return;
    }
    if (file.size > MAX_SIZE) {
      setError("Slika je prevelika. Maksimalna veličina je 5MB.");
      return;
    }

    setError(null);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setStage("preview");
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", selectedFile);
      const response = await ApiMethods.postFormData(
        "employee/photo",
        formData,
        token
      );
      localStorage.setItem("photoUrl", response.photo_url);
      window.dispatchEvent(
        new CustomEvent(PHOTO_UPDATED_EVENT, {
          detail: { photoUrl: response.photo_url },
        })
      );
      showAlert("Profilna slika je uspešno ažurirana.", "success");
      if (onUpdated) onUpdated(response.photo_url);
      onClose();
    } catch (err) {
      console.log(err);
      setError(
        (err && err.message) ||
          "Greška prilikom otpremanja slike. Pokušajte ponovo."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmRemove = async () => {
    setRemoving(true);
    try {
      const token = localStorage.getItem("token");
      await ApiMethods.delete("employee/photo", token);
      localStorage.removeItem("photoUrl");
      window.dispatchEvent(
        new CustomEvent(PHOTO_UPDATED_EVENT, { detail: { photoUrl: null } })
      );
      showAlert("Profilna slika je uspešno uklonjena.", "success");
      if (onUpdated) onUpdated(null);
      onClose();
    } catch (err) {
      console.log(err);
      showAlert("Greška prilikom uklanjanja slike", "error");
      setStage("menu");
    } finally {
      setRemoving(false);
    }
  };

  const hiddenInputs = (
    <>
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />
    </>
  );

  const menuContent = (
    <>
      <button
        type="button"
        className="hf-pp-row"
        onClick={() => galleryInputRef.current && galleryInputRef.current.click()}
      >
        <ImageOutlinedIcon fontSize="small" />
        <span>Izaberi iz galerije</span>
      </button>
      <button
        type="button"
        className="hf-pp-row"
        onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
      >
        <PhotoCameraOutlinedIcon fontSize="small" />
        <span>Slikaj</span>
      </button>
      {currentPhotoUrl && (
        <button
          type="button"
          className="hf-pp-row hf-pp-row-danger"
          onClick={() => setStage("removeConfirm")}
        >
          <DeleteOutlineIcon fontSize="small" />
          <span>Ukloni trenutnu sliku</span>
        </button>
      )}
      {error && <div className="hf-pp-error">{error}</div>}
    </>
  );

  const previewContent = (
    <>
      <div className="hf-pp-preview-wrap">
        <img className="hf-pp-preview-img" src={previewUrl} alt="Pregled" />
      </div>
      {error && <div className="hf-pp-error">{error}</div>}
      <button
        type="button"
        className="hf-pp-confirm-btn"
        disabled={uploading}
        onClick={handleConfirmUpload}
      >
        {uploading && <span className="hf-btn-spinner" />}
        Potvrdi
      </button>
      <button type="button" className="hf-pp-row" onClick={resetToMenu}>
        <ImageOutlinedIcon fontSize="small" />
        <span>Izaberi drugu sliku</span>
      </button>
    </>
  );

  if (stage === "removeConfirm") {
    return (
      <DeletePopUp
        message="Da li ste sigurni da želite da uklonite profilnu fotografiju?"
        onConfirm={handleConfirmRemove}
        closePopUp={() => (removing ? null : setStage("menu"))}
      />
    );
  }

  if (isDesktopTablet) {
    return (
      <div className="hf-pp-overlay hf-pp-overlay-center">
        {hiddenInputs}
        <div className="hf-pp-modal" ref={modalRef}>
          <div className="hf-pp-modal-header">
            <h2>Profilna fotografija</h2>
            <IconButton
              className="hf-pp-close"
              aria-label="Zatvori"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div className="hf-pp-modal-body">
            {stage === "menu" ? menuContent : previewContent}
          </div>
          {stage === "menu" && (
            <div className="hf-pp-modal-footer">
              <button
                type="button"
                className="hf-pp-cancel-btn"
                onClick={onClose}
              >
                Otkaži
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="hf-pp-overlay">
      {hiddenInputs}
      <div className="hf-pp-sheet">
        <div className="hf-pp-sheet-handle" />
        <div className="hf-pp-sheet-title">Profilna fotografija</div>
        {stage === "menu" ? menuContent : previewContent}
        <button
          type="button"
          className="hf-pp-cancel-btn hf-pp-sheet-cancel"
          onClick={onClose}
        >
          Otkaži
        </button>
      </div>
    </div>
  );
};

export default ProfilePhotoPicker;
